import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Trophy,
  Layers,
  Users,
  Share2,
  Crown,
  Calendar,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { usePageSeo } from '../hooks/usePageSeo';
import { buildSummaryInsights } from '../utils/summaryInsights';
import { createCategorySlug } from '../utils/urlHelpers';
import { buildOgImageUrl } from '../utils/ogImage';
import { absoluteUrl } from '../utils/seo';
import type { ForumBase } from '../types/forum';

interface Props {
  darkMode: boolean;
}

const SummaryPage: React.FC<Props> = ({ darkMode }) => {
  const { period } = useParams<{ period: string }>();
  const isWeekly = period === 'weekly';
  const [forums, setForums] = useState<ForumBase[]>([]);
  const [loading, setLoading] = useState(true);

  const insights = useMemo(
    () => (forums.length ? buildSummaryInsights(forums, isWeekly) : null),
    [forums, isWeekly]
  );

  const pageTitle = isWeekly ? 'Haftalık Forum Özeti' : 'Aylık Forum Özeti';
  const canonical = `/summary/${isWeekly ? 'weekly' : 'monthly'}`;

  usePageSeo({
    title: pageTitle,
    description:
      insights?.topRiser && insights.champion
        ? `${insights.periodLabel}: ${insights.champion.name} skor lideri, en hızlı büyüyen ${insights.topRiser.name} (+%${insights.topRiser.growthPercent.toFixed(1)} üye). Türk forum ekosistemi özeti.`
        : 'En çok büyüyen ve düşen Türkçe forum siteleri — paylaşılabilir dönem özeti.',
    keywords: 'forum özeti, yükselen forumlar, düşen forumlar, türk forum istatistikleri',
    canonical,
    ogImage:
      insights?.topRiser && insights.champion
        ? buildOgImageUrl({
            title: pageTitle,
            subtitle: insights.membersGrowthPct
              ? `Ekosistem üye: ${insights.membersGrowthPct}`
              : `${insights.totalForums} aktif forum`,
            line1: insights.champion ? `Şampiyon: ${insights.champion.name}` : undefined,
            line2: insights.topRiser
              ? `Büyüme: ${insights.topRiser.name} +%${insights.topRiser.growthPercent.toFixed(1)}`
              : undefined,
            line3: insights.decliningCategory
              ? `Düşen kategori: ${insights.decliningCategory.name}`
              : undefined,
            badge: insights.periodLabel,
          })
        : undefined,
  });

  useEffect(() => {
    supabase
      .from('forums')
      .select('*')
      .eq('is_active', true)
      .then(({ data }) => {
        setForums(data || []);
        setLoading(false);
      });
  }, []);

  const hero = darkMode
    ? 'bg-gradient-to-br from-indigo-950 via-gray-900 to-violet-950 border-indigo-900/50 text-white'
    : 'bg-gradient-to-br from-indigo-600 to-violet-700 text-white';
  const card = darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200 shadow-sm';
  const statBox = darkMode
    ? 'bg-gray-800/90 border-gray-700'
    : 'bg-white/95 border-white/30 shadow-lg text-gray-900';

  const shareUrl = absoluteUrl(canonical);

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen overflow-x-hidden ${darkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900'}`}>
      <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8 max-w-4xl">
        <Link
          to="/"
          className={`inline-flex items-center gap-2 mb-4 text-sm ${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
        >
          <ArrowLeft size={18} />
          Anasayfa
        </Link>

        <header className={`rounded-2xl border p-5 sm:p-8 mb-6 ${hero}`}>
          <div className="flex items-start gap-3">
            <Calendar className="text-indigo-200 shrink-0" size={32} />
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">{pageTitle}</h1>
              <p className="mt-2 text-sm sm:text-base text-indigo-100 max-w-xl">
                Otomatik üretilen dönem özeti — paylaşım ve SEO için özet kartlar.
              </p>
            </div>
          </div>
          {insights && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mt-5">
              <div className={`rounded-xl border p-3 min-w-0 ${statBox}`}>
                <Users size={18} className="text-indigo-500" />
                <p className="text-xs mt-1 opacity-80">Aktif forum</p>
                <p className="text-lg font-bold tabular-nums">{insights.totalForums}</p>
              </div>
              <div className={`rounded-xl border p-3 min-w-0 ${statBox}`}>
                <TrendingUp size={18} className="text-green-500" />
                <p className="text-xs mt-1 opacity-80">Toplam üye</p>
                <p className="text-lg font-bold tabular-nums truncate">
                  {insights.totalMembers.toLocaleString('tr-TR')}
                </p>
              </div>
              <div className={`rounded-xl border p-3 min-w-0 ${statBox}`}>
                <Layers size={18} className="text-indigo-500" />
                <p className="text-xs mt-1 opacity-80">Üye değişimi</p>
                <p className="text-lg font-bold">{insights.membersGrowthPct ?? '—'}</p>
              </div>
              <div className={`rounded-xl border p-3 min-w-0 ${statBox}`}>
                <Trophy size={18} className="text-amber-500" />
                <p className="text-xs mt-1 opacity-80">Büyüyen</p>
                <p className="text-lg font-bold tabular-nums">{insights.rising.length}</p>
              </div>
            </div>
          )}
        </header>

        {insights && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {insights.champion && (
                <article
                  className={`rounded-2xl border p-5 sm:p-6 relative overflow-hidden ${card} ${
                    darkMode ? '' : 'border-amber-200'
                  }`}
                >
                  <div
                    className={`absolute top-0 right-0 w-24 h-24 rounded-bl-full opacity-20 ${
                      darkMode ? 'bg-amber-500' : 'bg-amber-400'
                    }`}
                  />
                  <Crown className="text-amber-500 mb-2" size={28} />
                  <p className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                    {insights.periodLabel} — skor şampiyonu
                  </p>
                  <Link
                    to={`/forum/${insights.champion.forum_id}`}
                    className={`block mt-2 text-xl font-bold hover:underline ${darkMode ? 'text-white' : 'text-gray-900'}`}
                  >
                    {insights.champion.name}
                  </Link>
                  {insights.champion.growthPercent > 0 && (
                    <p className="text-green-500 text-sm mt-2 font-medium">
                      +%{insights.champion.growthPercent.toFixed(1)} üye (dönem)
                    </p>
                  )}
                </article>
              )}

              {insights.topRiser && (
                <article className={`rounded-2xl border p-5 sm:p-6 ${card}`}>
                  <TrendingUp className="text-green-500 mb-2" size={28} />
                  <p className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                    En hızlı büyüyen
                  </p>
                  <Link
                    to={`/forum/${insights.topRiser.forum_id}`}
                    className={`block mt-2 text-xl font-bold hover:underline ${darkMode ? 'text-white' : 'text-gray-900'}`}
                  >
                    {insights.topRiser.name}
                  </Link>
                  <p className="text-green-500 text-lg font-bold mt-2">
                    +%{insights.topRiser.growthPercent.toFixed(1)} üye
                  </p>
                </article>
              )}

              {insights.growingCategory && (
                <article className={`rounded-2xl border p-5 sm:p-6 ${card}`}>
                  <Layers className="text-indigo-500 mb-2" size={24} />
                  <p className={`text-xs font-semibold uppercase ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                    En çok büyüyen kategori
                  </p>
                  <Link
                    to={`/kategori/${createCategorySlug(insights.growingCategory.name)}/istatistikler`}
                    className={`block mt-2 text-lg font-bold hover:underline ${darkMode ? 'text-white' : 'text-gray-900'}`}
                  >
                    {insights.growingCategory.name}
                  </Link>
                  <p className="text-green-500 text-sm mt-2 font-medium">
                    +%{insights.growingCategory.growthPercent.toFixed(1)} toplam üye
                  </p>
                </article>
              )}

              {insights.decliningCategory && (
                <article className={`rounded-2xl border p-5 sm:p-6 ${card}`}>
                  <TrendingDown className="text-red-400 mb-2" size={24} />
                  <p className={`text-xs font-semibold uppercase ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                    En çok düşen kategori
                  </p>
                  <Link
                    to={`/kategori/${createCategorySlug(insights.decliningCategory.name)}/istatistikler`}
                    className={`block mt-2 text-lg font-bold hover:underline ${darkMode ? 'text-white' : 'text-gray-900'}`}
                  >
                    {insights.decliningCategory.name}
                  </Link>
                  <p className="text-red-500 text-sm mt-2 font-medium">
                    %{insights.decliningCategory.growthPercent.toFixed(1)} toplam üye
                  </p>
                </article>
              )}
            </div>

            <div
              className={`rounded-xl border p-4 mb-8 flex flex-col sm:flex-row sm:items-center gap-3 ${card}`}
            >
              <Share2 className="text-indigo-500 shrink-0" size={22} />
              <p className={`text-sm flex-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Bu özeti paylaşın:{' '}
                <span className={`font-mono text-xs break-all ${darkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>
                  {shareUrl}
                </span>
              </p>
              <div className="flex gap-2 shrink-0">
                <Link
                  to="/rising"
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700"
                >
                  Yükselenler
                </Link>
                <Link
                  to="/leaderboard"
                  className={`px-4 py-2 rounded-lg text-sm font-medium border ${
                    darkMode ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  Lig tablosu
                </Link>
              </div>
            </div>

            <section className="mb-10">
              <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                En çok büyüyen forumlar
              </h2>
              <ol className="space-y-2">
                {insights.rising.map((f, i) => (
                  <li key={f.id} className={`rounded-xl border p-4 flex items-center gap-4 ${card}`}>
                    <span
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                        darkMode ? 'bg-indigo-900 text-indigo-300' : 'bg-indigo-100 text-indigo-700'
                      }`}
                    >
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/forum/${f.forum_id}`}
                        className={`font-semibold hover:underline ${darkMode ? 'text-white' : 'text-gray-900'}`}
                      >
                        {f.name}
                      </Link>
                      <p className={`text-xs truncate ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                        {f.category}
                      </p>
                    </div>
                    <span className="text-green-500 font-bold text-sm shrink-0">
                      +%{f.growthPercent.toFixed(1)}
                    </span>
                  </li>
                ))}
              </ol>
            </section>

            {insights.declining.length > 0 && (
              <section>
                <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Düşüşte olanlar
                </h2>
                <ul className="space-y-2">
                  {insights.declining.map((f) => (
                    <li key={f.id} className={`rounded-xl border p-4 ${card}`}>
                      <Link
                        to={`/forum/${f.forum_id}`}
                        className={`font-medium hover:underline ${darkMode ? 'text-white' : 'text-gray-900'}`}
                      >
                        {f.name}
                      </Link>
                      <span className="text-red-500 ml-2 text-sm font-medium">
                        %{f.growthPercent.toFixed(1)}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SummaryPage;
